import React, { useEffect, useRef, useState } from "react";
import { ArrowLeftRight, Upload, X, Loader2, Download, FolderInput, CheckCircle2, AlertTriangle, FileText, Image as ImageIcon, Music, Film, Layers } from "lucide-react";
import { toast } from "sonner";
import { dapi as api } from "./deskApi";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

const CATS = {
  pictures: {
    label: "Pictures", icon: ImageIcon, accept: "image/*", multi: true,
    ops: [["img_jpg", "Picture → JPG"], ["img_png", "Picture → PNG"], ["img_webp", "Picture → WEBP"], ["img_bmp", "Picture → BMP"], ["img_tiff", "Picture → TIFF"], ["img_gif", "Picture → GIF"], ["img_ico", "Picture → ICO (icon)"], ["images_to_pdf", "Pictures → one PDF"]],
    note: "Add several pictures at once — single conversions come back as a ZIP.",
  },
  documents: {
    label: "Documents", icon: FileText, accept: ".pdf,.docx", multi: true,
    ops: [["pdf_to_jpg", "PDF → JPG pages"], ["pdf_to_png", "PDF → PNG pages"], ["pdf_to_txt", "PDF → Text"], ["docx_to_pdf", "Word (.docx) → PDF"], ["docx_to_txt", "Word (.docx) → Text"], ["docx_to_html", "Word (.docx) → HTML"]],
    note: "Multi-page PDFs convert every page (ZIP when more than one).",
  },
  audio: {
    label: "Audio", icon: Music, accept: "audio/*,video/*", multi: true,
    ops: [["audio_mp3", "→ MP3"], ["audio_wav", "→ WAV"], ["audio_ogg", "→ OGG"], ["audio_m4a", "→ M4A"], ["audio_flac", "→ FLAC"]],
    note: "Tip: drop a VIDEO here with → MP3 to extract its soundtrack.",
  },
  video: {
    label: "Video", icon: Film, accept: "video/*", multi: true,
    ops: [["video_mp4", "→ MP4"], ["video_webm", "→ WEBM"], ["video_mov", "→ MOV"], ["video_avi", "→ AVI"], ["video_gif", "→ GIF (animated)"]],
    note: "Big videos can take a minute or two — leave the window open.",
  },
  combine: {
    label: "Combine", icon: Layers, accept: "*/*", multi: true,
    ops: [["merge_pdf", "Merge PDFs → one PDF"], ["images_to_pdf", "Pictures → one PDF"], ["concat_audio", "Join audio → one MP3"], ["concat_video", "Join videos → one MP4"]],
    note: "Files are combined in the order listed — add them in the order you want.",
  },
};

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** All-in-one converter: pictures, documents, audio, video + combining. */
export default function ConverterApp() {
  const [cat, setCat] = useState("pictures");
  const [op, setOp] = useState(CATS.pictures.ops[0][0]);
  const [files, setFiles] = useState([]);
  const [job, setJob] = useState(null); // {id, status, result_name, result_size, fm_ok, error, secs}
  const inRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const pickCat = (k) => { setCat(k); setOp(CATS[k].ops[0][0]); setFiles([]); setJob(null); };

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    for (const f of arr) {
      if (f.size > 50 * 1024 * 1024) { toast.error(`"${f.name}" is over the 50MB limit.`); return; }
    }
    setFiles((prev) => [...prev, ...arr]);
    setJob(null);
  };

  const start = async () => {
    const fd = new FormData();
    fd.append("op", op);
    files.forEach((f) => fd.append("files", f));
    setJob({ status: "processing", secs: 0 });
    try {
      const { data } = await api.post("/desktop/convert", fd);
      const jid = data.job_id;
      const t0 = Date.now();
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/desktop/convert/${jid}`);
          if (st.status === "processing") {
            setJob({ id: jid, status: "processing", secs: Math.round((Date.now() - t0) / 1000) });
            return;
          }
          clearInterval(pollRef.current);
          setJob({ id: jid, ...st });
          if (st.status === "done") toast.success(`Done: ${st.result_name}`);
        } catch (e) {
          clearInterval(pollRef.current);
          setJob({ status: "error", error: formatErr(e.response?.data?.detail) || e.message });
        }
      }, 2000);
    } catch (e) {
      setJob({ status: "error", error: formatErr(e.response?.data?.detail) || e.message });
    }
  };

  const download = async () => {
    try {
      const r = await api.get(`/desktop/convert/${job.id}/download`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = job.result_name;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const saveFm = async () => {
    try {
      const { data } = await api.post(`/desktop/convert/${job.id}/save`);
      toast.success(`Saved to File Manager → "${data.folder}".`);
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const c = CATS[cat];
  const needTwo = ["merge_pdf", "concat_audio", "concat_video"].includes(op);
  const canStart = files.length >= (needTwo ? 2 : 1) && job?.status !== "processing";

  return (
    <div className="h-full flex flex-col bg-white" data-testid="converter-app">
      {/* Category tabs */}
      <div className="flex border-b bg-slate-50">
        {Object.entries(CATS).map(([k, v]) => (
          <button key={k} type="button" onClick={() => pickCat(k)}
            className={`flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${cat === k ? "border-fuchsia-600 text-fuchsia-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            data-testid={`converter-cat-${k}`}>
            <v.icon className="w-4 h-4" /> {v.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Operation */}
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">Conversion</label>
          <select value={op} onChange={(e) => { setOp(e.target.value); setJob(null); }}
            className="mt-1 block w-full max-w-sm text-sm border border-slate-300 rounded-sm px-2 py-2 bg-white"
            data-testid="converter-op-select">
            {c.ops.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <p className="text-xs text-slate-400 mt-1">{c.note}</p>
        </div>

        {/* Files */}
        <div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => inRef.current?.click()} data-testid="converter-add-files">
              <Upload className="w-4 h-4 mr-2" /> Add files
            </Button>
            {files.length > 0 && (
              <button type="button" onClick={() => { setFiles([]); setJob(null); }} className="text-xs text-slate-400 hover:text-red-600" data-testid="converter-clear-files">
                Clear all
              </button>
            )}
            <input ref={inRef} type="file" multiple accept={c.accept} className="hidden"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
          </div>
          {files.length > 0 && (
            <ul className="mt-2 space-y-1 max-w-lg" data-testid="converter-file-list">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm border border-slate-200 rounded-sm px-2.5 py-1.5 bg-slate-50">
                  <span className="w-5 text-[11px] text-slate-400 shrink-0">{i + 1}.</span>
                  <span className="truncate flex-1" title={f.name}>{f.name}</span>
                  <span className="text-xs text-slate-400 shrink-0">{fmtSize(f.size)}</span>
                  <button type="button" onClick={() => setFiles((prev) => prev.filter((_, x) => x !== i))} className="p-0.5 text-slate-400 hover:text-red-600" data-testid={`converter-remove-${i}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {needTwo && files.length < 2 && (
            <p className="text-xs text-amber-600 mt-1.5">Add at least two files to combine.</p>
          )}
        </div>

        <Button className="rounded-sm bg-fuchsia-700 hover:bg-fuchsia-800" disabled={!canStart} onClick={start} data-testid="converter-start">
          {job?.status === "processing" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowLeftRight className="w-4 h-4 mr-2" />}
          Convert
        </Button>

        {/* Result / status */}
        {job?.status === "processing" && (
          <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="converter-processing">
            <Loader2 className="w-4 h-4 animate-spin text-fuchsia-600" />
            Converting… {job.secs > 0 ? `${job.secs}s` : ""} — big videos can take a minute or two.
          </div>
        )}
        {job?.status === "error" && (
          <div className="flex items-start gap-2 text-sm text-red-700 border border-red-200 bg-red-50 rounded-sm p-3 max-w-lg" data-testid="converter-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{job.error || "Conversion failed."}</span>
          </div>
        )}
        {job?.status === "done" && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3 max-w-lg" data-testid="converter-result">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
              <CheckCircle2 className="w-4 h-4" /> {job.result_name}
              <span className="text-xs font-normal text-emerald-700">({fmtSize(job.result_size)})</span>
            </div>
            <div className="flex items-center gap-2 mt-2.5">
              <Button size="sm" className="rounded-sm bg-emerald-700 hover:bg-emerald-800" onClick={download} data-testid="converter-download">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download
              </Button>
              {job.fm_ok ? (
                <Button size="sm" variant="outline" className="rounded-sm" onClick={saveFm} data-testid="converter-save-fm">
                  <FolderInput className="w-3.5 h-3.5 mr-1.5" /> Save to File Manager
                </Button>
              ) : (
                <span className="text-xs text-slate-500">Over the File Manager's 10MB limit — download instead.</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
